// KidScheduleModal — Feb 2026 (Randy). 4-week availability grid per household
// member. Tabs Week 1 / Week 2 / Week 3 / Week 4, each with a 7-day Sun-Sat
// Yes/No toggle row. After filling Week 1, tap "Dup Week 1 → 2,3,4" to
// copy forward; every tab remains independently editable. Extras: a free-
// text `notes` field per person ("Wednesdays 5-7pm soccer practice") and
// a "Copy schedule from ___" dropdown that clones another person's grid.
//
// Backend contract: POST /calendar/{silo}/kid-availability/{target_id} with
// { availability: {"1":[7 bools], "2":[7 bools], "3":[7 bools], "4":[7 bools]}, notes: string }
import { useEffect, useMemo, useState } from "react";
import { X, Spinner, Copy, CalendarBlank } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useThemeLabel } from "@/lib/CalendarThemeProvider";
import * as T from "@/lib/themeManifest";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKS = ["1", "2", "3", "4"];

const emptyAvail = () => ({
  "1": [true, true, true, true, true, true, true],
  "2": [true, true, true, true, true, true, true],
  "3": [true, true, true, true, true, true, true],
  "4": [true, true, true, true, true, true, true],
});

function normalize(a) {
  if (!a || typeof a !== "object") return emptyAvail();
  const out = emptyAvail();
  for (const wk of WEEKS) {
    const row = Array.isArray(a[wk]) ? a[wk] : null;
    if (row) {
      const trimmed = row.slice(0, 7);
      while (trimmed.length < 7) trimmed.push(true);
      out[wk] = trimmed.map((v) => v !== false);
    }
  }
  return out;
}

export default function KidScheduleModal({
  siloId, targetId, targetName,
  initialAvailability, initialNotes,
  copyCandidates = [],   // [{ id, name, availability, notes }]
  onClose, onSaved,
}) {
  // Jun 2026 (Randy) — Theme QA dynamic-word sweep.
  const tScheduleBtn  = useThemeLabel(T.THEME_KEY_HOUSEHOLD_SCHEDULE_BUTTON);
  const tTitleSuffix  = useThemeLabel(T.THEME_KEY_KIDMODAL_SCHEDULE_TITLE_SUFFIX);
  const tIntro        = useThemeLabel(T.THEME_KEY_KIDMODAL_SCHEDULE_INTRO);
  const tNotesPh      = useThemeLabel(T.THEME_KEY_KIDMODAL_SCHEDULE_NOTES_PLACEHOLDER);
  const [avail, setAvail] = useState(() => normalize(initialAvailability));
  const [notes, setNotes] = useState(initialNotes || "");
  const [wk, setWk] = useState("1");
  const [busy, setBusy] = useState(false);
  const [copyFrom, setCopyFrom] = useState("");

  useEffect(() => {
    setAvail(normalize(initialAvailability));
    setNotes(initialNotes || "");
    setWk("1");
    setCopyFrom("");
  }, [targetId, initialAvailability, initialNotes]);

  const toggleDay = (day) => {
    setAvail((cur) => {
      const next = { ...cur, [wk]: cur[wk].slice() };
      next[wk][day] = !next[wk][day];
      return next;
    });
  };
  const dupWeekOne = () => {
    setAvail((cur) => ({
      "1": cur["1"].slice(),
      "2": cur["1"].slice(),
      "3": cur["1"].slice(),
      "4": cur["1"].slice(),
    }));
    toast.success("Week 1 copied to Weeks 2, 3, 4. Tap any tab to fine-tune.");
  };
  const clearWeek = () => {
    setAvail((cur) => ({ ...cur, [wk]: [false, false, false, false, false, false, false] }));
  };
  const fillWeek = () => {
    setAvail((cur) => ({ ...cur, [wk]: [true, true, true, true, true, true, true] }));
  };

  const applyCopy = (fromId) => {
    if (!fromId) return;
    const cand = copyCandidates.find((c) => c.id === fromId);
    if (!cand) return;
    setAvail(normalize(cand.availability));
    if (cand.notes && !notes.trim()) setNotes(cand.notes);
    toast.success(`Copied ${cand.name}'s schedule — edit any tab to customise.`);
  };

  const counts = useMemo(() => {
    const out = {};
    for (const w of WEEKS) out[w] = (avail[w] || []).filter(Boolean).length;
    return out;
  }, [avail]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/calendar/${siloId}/kid-availability/${targetId}`, {
        availability: avail,
        notes: notes.trim() || null,
      });
      toast.success(`Schedule saved for ${targetName || "member"}.`);
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FDFCF8] rounded-2xl w-full max-w-lg max-h-[90dvh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="kid-schedule-modal">
        <div className="px-5 py-3 border-b hairline flex items-center justify-between shrink-0">
          <h2 className="font-serif-display text-lg text-[#1C1B1A] flex items-center gap-2">
            <CalendarBlank size={16} weight="duotone" /> {targetName ? `${targetName}'s ${tTitleSuffix}` : tScheduleBtn}
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#F0F1EE]" data-testid="kid-schedule-close"><X size={18} /></button>
        </div>

        <div className="px-5 pt-3 pb-2 shrink-0">
          <p className="text-[11px] text-[#6B6A68] leading-relaxed">
            {tIntro}
          </p>
          {copyCandidates.length > 0 && (
            <div className="mt-2 flex items-center gap-2">
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68] flex items-center gap-1">
                <Copy size={11} /> Copy from
              </label>
              <select value={copyFrom} onChange={(e) => { setCopyFrom(e.target.value); applyCopy(e.target.value); }}
                data-testid="kid-schedule-copy-from"
                className="text-[11px] px-2 py-1 border hairline rounded bg-white">
                <option value="">— pick a person —</option>
                {copyCandidates.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
        </div>

        <div className="px-5 shrink-0">
          <div className="flex rounded-md border hairline overflow-hidden" data-testid="kid-schedule-week-tabs">
            {WEEKS.map((w) => (
              <button key={w} type="button" onClick={() => setWk(w)}
                data-testid={`kid-schedule-week-${w}`}
                className={`flex-1 text-[11px] px-2 py-1.5 flex items-center justify-center gap-1.5 ${wk === w ? "bg-[#1C1B1A] text-white" : "text-[#6B6A68] hover:bg-[#F0F1EE]"}`}>
                <span>Week {w}</span>
                <span className={`text-[9px] tabular-nums ${wk === w ? "text-white/80" : "text-[#B5B3AE]"}`}>
                  {counts[w]}/7
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="px-5 py-3 flex-1 overflow-y-auto">
          <div className="grid grid-cols-7 gap-1.5">
            {DOW.map((label, i) => {
              const on = (avail[wk] || [])[i];
              return (
                <button key={label} type="button" onClick={() => toggleDay(i)}
                  data-testid={`kid-schedule-day-${wk}-${i}`}
                  className={`flex flex-col items-center gap-0.5 py-2 rounded border transition-colors ${on
                    ? "bg-[#EBF1EC] border-[#4A5D4E] text-[#4A5D4E]"
                    : "bg-white border-hairline text-[#B5B3AE] hover:border-[#C16E5A]"}`}>
                  <span className="text-[9px] uppercase tracking-[0.14em]">{label}</span>
                  <span className="text-[10px] font-medium">{on ? "Yes" : "No"}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <button onClick={fillWeek} data-testid="kid-schedule-fill-week"
              className="text-[10px] px-2 py-1 rounded border hairline hover:border-[#4A5D4E]">All Yes</button>
            <button onClick={clearWeek} data-testid="kid-schedule-clear-week"
              className="text-[10px] px-2 py-1 rounded border hairline hover:border-[#C16E5A]">All No</button>
            {wk === "1" && (
              <button onClick={dupWeekOne} data-testid="kid-schedule-dup-week1"
                className="text-[10px] px-2 py-1 rounded bg-[#F8F1E2] text-[#8A6A24] border border-[#EAD9AE] hover:bg-[#F3E7CB] flex items-center gap-1">
                <Copy size={11} /> Dup Week 1 → 2, 3, 4
              </button>
            )}
          </div>

          <label className="block mt-4">
            <span className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68]">
              Notes for the AI (optional)
            </span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder={tNotesPh}
              maxLength={400} rows={2} data-testid="kid-schedule-notes"
              className="mt-1 w-full px-2 py-1.5 border hairline rounded text-xs bg-white focus:outline-none focus:border-[#4A5D4E]" />
          </label>
        </div>

        <div className="px-5 py-3 border-t hairline flex items-center justify-end gap-2 shrink-0 pb-[calc(0.75rem_+_env(safe-area-inset-bottom))]">
          <button onClick={onClose} className="text-xs px-3 py-2 rounded text-[#6B6A68]">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="kid-schedule-save"
            className="text-xs px-4 py-2 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5">
            {busy && <Spinner size={12} className="animate-spin" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}
